import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to check localStorage for tokens
const getTokenFromStorage = () => {
  const crewToken = localStorage.getItem('crewToken');
  const ownerToken = localStorage.getItem('token');
  return crewToken || ownerToken;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getTokenFromStorage);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(() => localStorage.getItem('demoMode') === 'true');
  const [isCrew, setIsCrew] = useState(!!localStorage.getItem('crewToken'));
  // Force re-render counter to update isAuthenticated
  const [authVersion, setAuthVersion] = useState(0);

  // Function to check/refresh auth state
  const checkAuth = useCallback(async () => {
    const crewToken = localStorage.getItem('crewToken');
    const ownerToken = localStorage.getItem('token');
    const savedToken = crewToken || ownerToken;
    
    // Always update token state from localStorage first
    setToken(savedToken);
    setIsCrew(!!crewToken);
    setAuthVersion(v => v + 1); // Force context consumers to re-read
    
    if (savedToken) {
      try {
        const endpoint = crewToken ? `${API}/api/crew/me` : `${API}/api/auth/me`;
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${savedToken}` }
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else if (response.status === 401 || response.status === 403) {
          if (crewToken) localStorage.removeItem('crewToken');
          if (ownerToken) localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setIsCrew(false);
        }
      } catch (err) {
      }
    } else {
      setToken(null);
      setUser(null);
      setIsCrew(false);
    }
    setLoading(false);
  }, []);

  // Initial auth check on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for storage changes (cross-tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'crewToken' || e.key === 'token') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth]);

  const login = useCallback(async (email, password, captchaToken, captchaAnswer) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status !== 200) {
            reject(new Error(data.detail || 'Login failed'));
            return;
          }
          
          if (data.user?.is_crew) {
            localStorage.setItem('crewToken', data.access_token);
            localStorage.setItem('crewInfo', JSON.stringify(data.user));
            window.location.href = '/crew-dashboard';
            return;
          }
          
          localStorage.setItem('token', data.access_token);
          setToken(data.access_token);
          setUser(data.user);
          setIsDemo(false);
          localStorage.removeItem('demoMode');
          setAuthVersion(v => v + 1);
          resolve(data.user);
        } catch (e) {
          reject(new Error('Failed to parse response'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send(JSON.stringify({ email, password, captcha_token: captchaToken || '', captcha_answer: captchaAnswer || '' }));
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('crewToken');
    localStorage.removeItem('crewInfo');
    setToken(null);
    setUser(null);
    setIsDemo(false);
    localStorage.removeItem('demoMode');
    setIsCrew(false);
    setAuthVersion(v => v + 1);
  }, []);

  const enterDemoMode = useCallback(() => {
    setIsDemo(true);
    localStorage.setItem('demoMode', 'true');
    setUser(null);
    setToken(null);
    setIsCrew(false);
    localStorage.removeItem('token');
    localStorage.removeItem('crewToken');
    setAuthVersion(v => v + 1);
  }, []);

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...options.headers };
    const currentToken = getTokenFromStorage();
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }
    return fetch(url, { ...options, headers });
  }, []);

  // Compute isAuthenticated - check state AND localStorage
  const isAuthenticated = useMemo(() => {
    // First check React state
    if (user || token) return true;
    // Fallback: check localStorage directly (for post-login navigation)
    return !!getTokenFromStorage();
  }, [user, token, authVersion]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isDemo,
    isCrew,
    isAuthenticated,
    isAdmin: user?.role === 'admin',
    login,
    logout,
    enterDemoMode,
    authFetch,
    checkAuth
  }), [user, token, loading, isDemo, isCrew, isAuthenticated, login, logout, enterDemoMode, authFetch, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
